import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Lightning,
  Clock,
  FunnelSimple,
  PencilSimple,
  CheckCircle,
  ArrowsClockwise,
  Note,
  Plus,
  WarningCircle,
  CalendarBlank,
  FlagBanner
} from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import QuickUpdateModal from '@/components/QuickUpdateModal';

const API = `${window.location.origin}/api`;

const ACTION_LABELS = {
  task_created: 'Created',
  note_added: 'Note added',
  note_edited: 'Note edited',
  status_changed: 'Status changed',
  task_completed: 'Completed',
  task_reopened: 'Reopened',
  priority_changed: 'Priority changed',
  due_date_changed: 'Date changed',
  title_changed: 'Title edited',
  description_changed: 'Description edited',
  project_changed: 'Project changed',
  manual_update: 'Updated',
  subtask_added: 'Sub-task added',
  subtask_completed: 'Sub-task done',
  subtask_reopened: 'Sub-task reopened',
  subtask_updated: 'Sub-task updated',
  subtask_removed: 'Sub-task removed',
};

const ACTION_COLORS = {
  task_created: 'bg-blue-100 text-blue-700',
  note_added: 'bg-purple-100 text-purple-700',
  note_edited: 'bg-purple-100 text-purple-700',
  status_changed: 'bg-blue-100 text-blue-700',
  task_completed: 'bg-emerald-100 text-emerald-700',
  task_reopened: 'bg-amber-100 text-amber-700',
  priority_changed: 'bg-orange-100 text-orange-700',
  due_date_changed: 'bg-green-100 text-green-700',
  title_changed: 'bg-amber-100 text-amber-700',
  description_changed: 'bg-amber-100 text-amber-700',
  project_changed: 'bg-indigo-100 text-indigo-700',
  manual_update: 'bg-slate-100 text-slate-700',
  subtask_added: 'bg-cyan-100 text-cyan-700',
  subtask_completed: 'bg-emerald-100 text-emerald-700',
  subtask_reopened: 'bg-amber-100 text-amber-700',
  subtask_updated: 'bg-slate-100 text-slate-700',
  subtask_removed: 'bg-red-100 text-red-700',
};

const PRIORITY_COLORS = {
  high: 'text-red-600',
  medium: 'text-amber-600',
  low: 'text-slate-500',
};

function TodayView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dueData, setDueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState('');
  const [quickUpdateTask, setQuickUpdateTask] = useState(null);

  const fetchToday = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterProject) params.project_client = filterProject;
      const [actRes, dueRes, projRes] = await Promise.all([
        axios.get(`${API}/activity/today`, { params, withCredentials: true }),
        axios.get(`${API}/activity/due-today`, { params, withCredentials: true }),
        axios.get(`${API}/tasks/projects`, { withCredentials: true })
      ]);
      setData(actRes.data);
      setDueData(dueRes.data);
      setProjects(projRes.data);
    } catch (error) {
      console.error('Failed to fetch today activity:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchToday(); }, [filterProject]);

  const formatTime = (ts) => {
    try { return format(parseISO(ts), 'h:mm a'); }
    catch { return ''; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  const totalDue = (dueData?.total_due || 0) + (dueData?.total_overdue || 0);
  const totalWorked = data?.total || 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="today-view">
      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Worked on</p>
          <p className="text-2xl font-bold text-slate-900" data-testid="worked-count">{totalWorked}</p>
          <p className="text-xs text-slate-500">task{totalWorked !== 1 ? 's' : ''} touched today</p>
        </div>
        <div className={`bg-white border rounded-lg p-4 ${totalDue > 0 ? 'border-amber-200' : 'border-slate-200'}`}>
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Needs attention</p>
          <p className={`text-2xl font-bold ${totalDue > 0 ? 'text-amber-600' : 'text-slate-900'}`} data-testid="due-count">{totalDue}</p>
          <p className="text-xs text-slate-500">
            {dueData?.total_due || 0} due today{dueData?.total_overdue > 0 ? ` · ${dueData.total_overdue} overdue` : ''}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <FunnelSimple size={16} />
          <span>Filter:</span>
        </div>
        <select
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700"
          data-testid="today-project-filter"
        >
          <option value="">All Projects</option>
          {projects.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Section 1: Due today / Needs attention */}
      {totalDue > 0 && (
        <div className="mb-6" data-testid="due-today-section">
          <div className="flex items-center gap-2 mb-3">
            <WarningCircle size={18} weight="fill" className="text-amber-500" />
            <h3 className="text-sm font-semibold text-slate-900">Due today / Needs attention</h3>
          </div>

          {/* Overdue */}
          {dueData?.overdue?.length > 0 && (
            <div className="mb-3">
              <p className="text-[11px] uppercase tracking-wider text-red-500 font-semibold mb-1.5">Overdue</p>
              <div className="space-y-2">
                {dueData.overdue.map((item) => (
                  <DueTaskCard key={item.task_id} item={item} isOverdue onUpdate={setQuickUpdateTask} />
                ))}
              </div>
            </div>
          )}

          {/* Due today */}
          {dueData?.due_today?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-amber-500 font-semibold mb-1.5">Due today</p>
              <div className="space-y-2">
                {dueData.due_today.map((item) => (
                  <DueTaskCard key={item.task_id} item={item} onUpdate={setQuickUpdateTask} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Section 2: Worked on today */}
      <div data-testid="worked-today-section">
        <div className="flex items-center gap-2 mb-3">
          <Lightning size={18} weight="fill" className="text-blue-500" />
          <h3 className="text-sm font-semibold text-slate-900">Worked on today</h3>
          <span className="text-xs text-slate-400 ml-auto" data-testid="today-count">{totalWorked} task{totalWorked !== 1 ? 's' : ''}</span>
        </div>

        {(!data?.items || data.items.length === 0) ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="today-empty">
            <Clock size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No activity yet today. Start by updating a task.</p>
            <button onClick={() => navigate('/tasks')} className="mt-3 text-sm text-navy hover:underline">Go to Tasks</button>
          </div>
        ) : (
          <div className="space-y-2">
            {data.items.map((item) => (
              <div
                key={item.task_id}
                className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                data-testid={`today-item-${item.task_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-slate-900 text-sm truncate">{item.title}</h3>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ACTION_COLORS[item.latest_action] || 'bg-slate-100 text-slate-700'}`}>
                        {ACTION_LABELS[item.latest_action] || item.latest_action}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                      {item.project_client && <span className="font-medium text-navy">{item.project_client}</span>}
                      <span>{item.status}</span>
                      <span className="text-slate-300">|</span>
                      <span>{formatTime(item.latest_timestamp)}</span>
                      {item.update_count > 1 && <span className="text-slate-400">({item.update_count} updates)</span>}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-1">{item.latest_update}</p>
                  </div>
                  <button
                    onClick={() => setQuickUpdateTask(item)}
                    className="flex-shrink-0 text-xs font-medium text-navy bg-blue-50 hover:bg-blue-100 transition-colors px-3 py-1.5 rounded-lg flex items-center gap-1"
                    data-testid={`quick-update-${item.task_id}`}
                  >
                    <Plus size={12} weight="bold" /> Update
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {quickUpdateTask && (
        <QuickUpdateModal
          taskId={quickUpdateTask.task_id}
          taskTitle={quickUpdateTask.title}
          currentStatus={quickUpdateTask.status}
          currentProject={quickUpdateTask.project_client}
          onClose={() => setQuickUpdateTask(null)}
          onComplete={() => { setQuickUpdateTask(null); fetchToday(); }}
        />
      )}
    </motion.div>
  );
}

function DueTaskCard({ item, isOverdue, onUpdate }) {
  return (
    <div className={`bg-white border rounded-lg p-3.5 hover:border-slate-300 transition-colors ${isOverdue ? 'border-red-200' : 'border-amber-200'}`} data-testid={`due-item-${item.task_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-semibold text-slate-900 text-sm truncate">{item.title}</h3>
            <span className={`text-xs font-medium ${PRIORITY_COLORS[item.priority] || 'text-slate-500'}`}>
              {item.priority}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {item.project_client && <span className="font-medium text-navy">{item.project_client}</span>}
            <span className="flex items-center gap-1">
              <CalendarBlank size={11} />
              {isOverdue ? <span className="text-red-500 font-medium">Overdue: {item.due_date}</span> : <span>Due today</span>}
            </span>
            {item.subtask_count > 0 && <span>{item.subtask_done}/{item.subtask_count} done</span>}
          </div>
        </div>
        <button
          onClick={() => onUpdate(item)}
          className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${isOverdue ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-amber-600 bg-amber-50 hover:bg-amber-100'}`}
          data-testid={`due-update-${item.task_id}`}
        >
          <Plus size={12} weight="bold" /> Update
        </button>
      </div>
    </div>
  );
}

export default TodayView;
